import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { animate, stagger } from "animejs";
import { Disc3 } from "lucide-react";
import { KandiBeadImage } from "../components/BeadSystem";
import { T } from "../theme";

/**
 * StorePage — the merch table. Public (logged in or not).
 *
 * Browse-only v1: models render from their real bead sequences via
 * BeadSystem (the bracelet is the product shot), packs of 3/5/10 with live
 * price math. Checkout intentionally absent — an honest "drops soon" line
 * holds the space; no fake buy buttons.
 *
 * Catalog comes from GET /api/store/products (Mongo-managed; see server.js
 * STORE_SEED for the launch lineup).
 */

const API = process.env.REACT_APP_API_URL || "";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Staggered bounce across a card's beads — the bracelet reacts to touch. */
function waveBeads(rootEl) {
  if (prefersReducedMotion() || !rootEl) return;
  const beads = rootEl.querySelectorAll(".store-bead");
  if (!beads.length) return;
  animate(beads, {
    translateY: [0, -6, 0],
    duration: 500,
    delay: stagger(36),
    ease: "inOut(2)",
    onComplete: () => beads.forEach((b) => { b.style.transform = ""; }),
  });
}

const perEach = (price, qty) => (price / qty).toFixed(2);

/** The best-value pack = lowest per-bracelet price. */
function bestValueQty(packs) {
  if (!packs?.length) return null;
  return packs.reduce((best, p) => (p.price / p.qty < best.price / best.qty ? p : best), packs[0]).qty;
}

// ─── Bead run — the product shot ─────────────────────────────────────────────

function BeadRun({ colors = [], shapes = [], size = 38 }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "6px 0" }}>
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: Math.round(size * 0.16) }}>
        <div aria-hidden style={{
          position: "absolute", left: -size * 0.2, right: -size * 0.2, top: "50%",
          height: 2, background: "rgba(255,255,255,0.12)", borderRadius: 2,
        }} />
        {colors.map((color, i) => (
          <div key={i} className="store-bead" style={{ zIndex: 1, display: "flex", flexShrink: 0 }}>
            <KandiBeadImage shapeType={shapes[i] || "pony"} color={color} size={size} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Pack selector — the toy ─────────────────────────────────────────────────

function PackSelector({ packs, accent, selectedQty, onSelect, name }) {
  const best = bestValueQty(packs);
  return (
    <div role="group" aria-label={`${name} pack size`} style={{
      display: "flex", gap: 6, padding: 5,
      background: "rgba(0,0,0,0.35)", borderRadius: 14,
      border: T.border,
    }}>
      {packs.map((p) => {
        const active = p.qty === selectedQty;
        return (
          <button
            key={p.qty}
            onClick={() => onSelect(p.qty)}
            aria-pressed={active}
            style={{
              flex: 1, minHeight: 48, padding: "6px 8px",
              borderRadius: 10, cursor: "pointer",
              background: active ? `${accent}26` : "transparent",
              border: active ? `1px solid ${accent}80` : "1px solid transparent",
              boxShadow: active ? `0 0 16px ${accent}33` : "none",
              transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 1,
            }}
          >
            <span style={{
              fontSize: "0.95rem", fontWeight: 800,
              color: active ? T.textPrimary : T.textStrong,
            }}>
              {p.qty}-pack
            </span>
            <span style={{ fontSize: "0.68rem", fontWeight: 600, color: active ? accent : T.textMuted }}>
              {p.qty === best ? "best value" : `$${p.price}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Price readout with a quick tick-up when the pack changes. */
function PriceReadout({ pack, accent }) {
  const priceRef = useRef(null);
  const prevPrice = useRef(pack.price);

  useEffect(() => {
    const el = priceRef.current;
    const from = prevPrice.current;
    prevPrice.current = pack.price;
    if (!el || from === pack.price || prefersReducedMotion()) return;
    const counter = { v: from };
    animate(counter, {
      v: pack.price,
      duration: 380,
      ease: "out(3)",
      onUpdate: () => { el.textContent = `$${Math.round(counter.v)}`; },
    });
  }, [pack.price]);

  return (
    <p style={{ margin: 0, display: "flex", alignItems: "baseline", gap: 10, justifyContent: "center" }}>
      <span ref={priceRef} style={{
        fontSize: "1.9rem", fontWeight: 800, color: T.textPrimary, lineHeight: 1,
        textShadow: `0 0 24px ${accent}59`,
      }}>
        ${pack.price}
      </span>
      <span style={{ fontSize: "0.85rem", color: T.textStrong }}>
        · ${perEach(pack.price, pack.qty)} per bracelet
      </span>
    </p>
  );
}

// ─── Model cards ──────────────────────────────────────────────────────────────

function useDefaultPack(packs) {
  // The middle pack anchors the pricing story
  const mid = packs[Math.min(1, packs.length - 1)];
  return useState(mid?.qty ?? packs[0]?.qty);
}

function HeadlinerCard({ product }) {
  const [qty, setQty] = useDefaultPack(product.packs);
  const pack = product.packs.find((p) => p.qty === qty) || product.packs[0];

  return (
    <section
      aria-labelledby={`store-${product.slug}`}
      className="store-card"
      onMouseEnter={(e) => waveBeads(e.currentTarget)}
      style={{
        position: "relative", overflow: "hidden",
        background: T.card, backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: `1px solid ${product.accent}40`,
        borderRadius: T.radiusLg,
        padding: "clamp(28px, 5vw, 52px) clamp(20px, 4vw, 48px)",
        marginBottom: 40,
        textAlign: "center",
      }}
    >
      {/* The model's own light washes its section */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(ellipse at 50% 0%, ${product.accent}24 0%, transparent 60%)`,
      }} />

      <p style={{
        position: "relative", margin: "0 0 18px",
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "5px 14px", borderRadius: 20,
        background: `${product.accent}1f`, border: `1px solid ${product.accent}59`,
        color: product.accent, fontSize: "0.72rem", fontWeight: 800,
        textTransform: "uppercase", letterSpacing: "0.14em",
      }}>
        <Disc3 size={12} aria-hidden /> Headliner
      </p>

      <div style={{ position: "relative" }}>
        <BeadRun colors={product.colors} shapes={product.shapes} size={54} />
      </div>

      <h2 id={`store-${product.slug}`} style={{
        position: "relative", margin: "18px 0 6px",
        fontSize: "clamp(1.7rem, 4.5vw, 2.4rem)", fontWeight: 800,
        color: T.textPrimary, textWrap: "balance",
      }}>
        {product.name}
      </h2>
      <p style={{ position: "relative", margin: "0 auto 26px", maxWidth: "48ch", color: T.textStrong, fontSize: "1rem", lineHeight: 1.5 }}>
        {product.tagline}
      </p>

      <div style={{ position: "relative", maxWidth: 420, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <PackSelector packs={product.packs} accent={product.accent} selectedQty={qty} onSelect={setQty} name={product.name} />
        <PriceReadout pack={pack} accent={product.accent} />
      </div>
    </section>
  );
}

function ModelTile({ product }) {
  const [qty, setQty] = useDefaultPack(product.packs);
  const pack = product.packs.find((p) => p.qty === qty) || product.packs[0];
  const [hov, setHov] = useState(false);

  return (
    <article
      aria-labelledby={`store-${product.slug}`}
      className="store-card"
      onMouseEnter={(e) => { setHov(true); waveBeads(e.currentTarget); }}
      onMouseLeave={() => setHov(false)}
      style={{
        background: T.card, backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: hov ? `1px solid ${product.accent}66` : T.border,
        borderRadius: T.radiusLg,
        padding: "26px 22px",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: hov ? `0 14px 40px ${product.accent}1f` : "0 4px 20px rgba(0,0,0,0.3)",
        display: "flex", flexDirection: "column", gap: 14,
        textAlign: "center",
      }}
    >
      <BeadRun colors={product.colors} shapes={product.shapes} size={38} />
      <div>
        <h2 id={`store-${product.slug}`} style={{
          margin: "6px 0 4px", fontSize: "1.25rem", fontWeight: 800,
          color: T.textPrimary, textWrap: "balance",
        }}>
          {product.name}
        </h2>
        <p style={{ margin: 0, color: T.textStrong, fontSize: "0.85rem", lineHeight: 1.45 }}>
          {product.tagline}
        </p>
      </div>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
        <PackSelector packs={product.packs} accent={product.accent} selectedQty={qty} onSelect={setQty} name={product.name} />
        <PriceReadout pack={pack} accent={product.accent} />
      </div>
    </article>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function StorePage() {
  const [products, setProducts] = useState(null); // null = loading
  const [error, setError] = useState(false);

  const fetchProducts = useCallback(async () => {
    setProducts(null);
    setError(false);
    try {
      const res = await axios.get(`${API}/api/store/products`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Store fetch error:", err);
      setError(true);
      setProducts([]);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Entrance: headliner leads, tiles cascade behind it
  useEffect(() => {
    if (!products?.length || prefersReducedMotion()) return;
    const cards = document.querySelectorAll(".store-card");
    if (!cards.length) return;
    cards.forEach((c) => { c.style.transition = "none"; });
    animate(cards, {
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 380,
      delay: stagger(70),
      ease: "out(3)",
      onComplete: () => cards.forEach((c) => {
        c.style.transition = "";
        c.style.transform = "";
        c.style.opacity = "";
      }),
    });
  }, [products]);

  const headliner = products?.find((p) => p.featured) || products?.[0];
  const rest = products?.filter((p) => p !== headliner) || [];

  return (
    <div style={{ minHeight: "100vh", background: T.bg, padding: "88px 16px 64px" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>

        {/* ── Header ── */}
        <header style={{ textAlign: "center", marginBottom: 44 }}>
          <h1 style={{
            margin: "0 0 10px",
            fontFamily: "var(--font-display)",
            fontSize: "clamp(1.9rem, 5vw, 2.8rem)", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 28px ${T.purple}60`,
            textWrap: "balance",
          }}>
            The Merch Table
          </h1>
          <p style={{ margin: "0 auto", maxWidth: "52ch", color: T.textStrong, fontSize: "1rem", lineHeight: 1.55 }}>
            Hand-strung kandi, made to trade. Pick a model, pick a pack —
            every bracelet ships scan-ready with its own digital soul.
          </p>
        </header>

        {/* ── Loading ── */}
        {products === null && (
          <div aria-label="Loading the merch table" role="status">
            <div className="feed-skeleton" style={{ height: 300, borderRadius: 24, marginBottom: 40 }} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="feed-skeleton" style={{ height: 280, borderRadius: 24 }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {error && products?.length === 0 && (
          <div role="alert" style={{
            textAlign: "center", padding: "56px 32px",
            background: `${T.red}0f`,
            border: `1px solid ${T.red}40`, borderRadius: 24,
          }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 14 }}>📡</div>
            <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
              The merch table's down
            </p>
            <p style={{ color: T.textStrong, margin: "0 0 24px", fontSize: "0.9rem" }}>
              Couldn't reach the store — give it another go ✌️
            </p>
            <button
              onClick={fetchProducts}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "12px 28px", minHeight: 44, border: "none",
                background: "linear-gradient(135deg, #ff3cac, #b44fff)",
                color: "#fff", cursor: "pointer",
                borderRadius: 50, fontWeight: 700, fontSize: "0.9rem",
                boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Empty (catalog exists but nothing active) ── */}
        {!error && products?.length === 0 && (
          <div style={{
            textAlign: "center", padding: "56px 32px",
            background: T.card, border: T.border, borderRadius: 24,
          }}>
            <div style={{ fontSize: "2.6rem", marginBottom: 14 }}>📦</div>
            <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
              Restocking the table
            </p>
            <p style={{ color: T.textStrong, margin: 0, fontSize: "0.9rem" }}>
              New drops are being strung right now — check back soon.
            </p>
          </div>
        )}

        {/* ── Catalog ── */}
        {products?.length > 0 && (
          <>
            {headliner && <HeadlinerCard product={headliner} />}

            {rest.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 18,
              }}>
                {rest.map((p) => <ModelTile key={p.slug} product={p} />)}
              </div>
            )}

            {/* Honest checkout note — holds the space, promises nothing fake */}
            <p style={{
              textAlign: "center", margin: "44px 0 0",
              color: T.textStrong, fontSize: "0.9rem",
            }}>
              Checkout drops soon ✌️ — every pack comes scan-ready for{" "}
              <span style={{ color: T.cyan, fontWeight: 700 }}>KandiKonnect</span> tracking.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default StorePage;
